import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import Icon from 'dpl/common/components/Icon';
import Drawer, {
  DrawerItem,
  DrawerItemContent,
  DrawerItemTitle
} from 'dpl/common/components/Drawer';

import {
  TRANSPORT_CATEGORY,
  getTransportModalInfo,
  TRANSPORT_TYPES
} from '../utils/constants';

const { AIR, GROUND, PICKUP } = TRANSPORT_TYPES;

function getDeliveryMethodKeys(deliveryMethods) {
  const allMethods = [];
  if (deliveryMethods[AIR]) {
    allMethods.push(...deliveryMethods[AIR]);
  }
  if (deliveryMethods[GROUND]) {
    allMethods.push(...deliveryMethods[GROUND]);
  }
  if (deliveryMethods[PICKUP]) {
    allMethods.push(...deliveryMethods[PICKUP]);
  }

  return allMethods.map(dm => dm.key);
}

function TransportationDetails({ iconName, name, section, transportMethods }) {
  return (
    <div className={classnames('mb5 mb11-md', name)}>
      <div className="flex items-center mb4">
        <div className="bg-pale-blue midnight pv1 ph1 br-100 mr3">
          <Icon name={iconName} width="24px" height="24px" />
        </div>
        <h5 className="f4 fw-medium">{section}</h5>
      </div>

      {transportMethods.map((tm, idx) => (
        <Drawer key={idx} scrollToTop={false}>
          <DrawerItem
            itemKey={tm.key}
            className={classnames({
              'bt b--subtle-gray': idx > 0
            })}
          >
            <DrawerItemTitle
              iconClassName="icon-default"
              iconOpenName="fetch-chevron-up"
              iconCloseName="fetch-chevron-down"
            >
              <div className="flex">
                <p className="flex-auto font-16">{tm.title}</p>
                {tm.tag && (
                  <span className="f1 stroll-dark bg-stroll-light ph4 pv1 mh3 br4 min-content-h self-center">
                    {tm.tag}
                  </span>
                )}
              </div>
            </DrawerItemTitle>
            <DrawerItemContent>
              <p className="f2">{tm.longDescription}</p>
              <a
                className="underline f2"
                href={tm.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {tm.linkText}
              </a>
            </DrawerItemContent>
          </DrawerItem>
        </Drawer>
      ))}
    </div>
  );
}

TransportationDetails.propTypes = {
  iconName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  section: PropTypes.string.isRequired,
  transportMethods: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default function TransportationModal({
  onCloseClick,
  deliveryMethods,
  breederFirstName,
  facilityLocation,
  puppyName
}) {
  let transportModalInfo = getTransportModalInfo({
    breederFirstName,
    puppyName,
    facilityLocation
  });

  if (deliveryMethods) {
    const deliveryMethodKeys = getDeliveryMethodKeys(deliveryMethods);
    const availableDeliveryMethods = transportModalInfo.filter(tm =>
      deliveryMethodKeys.includes(tm.key)
    );
    transportModalInfo = availableDeliveryMethods;
  }

  const pickUpTransportation = transportModalInfo.filter(
    tm => tm.type === TRANSPORT_CATEGORY.PICKUP
  );
  const dropOffTransportation = transportModalInfo.filter(
    tm => tm.type === TRANSPORT_CATEGORY.DROPOFF
  );

  return (
    <MiniOverlayModal
      className="TransportationModal"
      contentClassName="bg-white br3"
      onCloseClick={onCloseClick}
    >
      <div className="pv8 ph8 ph16-sm pv14-sm">
        <h2 className="f5 f6-sm mb2">Getting your puppy home</h2>
        <p className="mb8 font-16 secondary">
          You&apos;ll work directly with {breederFirstName || 'your breeder'} to
          decide how to bring your puppy home. You&apos;ll be able to ask
          questions and receive photos and videos before you get your puppy.
        </p>
        {dropOffTransportation.length > 0 && (
          <TransportationDetails
            name="AirTransportation"
            iconName="fetch-location"
            section="Drop off options"
            transportMethods={dropOffTransportation}
          />
        )}
        {pickUpTransportation.length > 0 && (
          <TransportationDetails
            name="PickupTransportation"
            iconName="fetch-transportation-car"
            section="Pick up options"
            transportMethods={pickUpTransportation}
          />
        )}
      </div>
    </MiniOverlayModal>
  );
}

TransportationModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  deliveryMethods: PropTypes.object,
  breederFirstName: PropTypes.string,
  facilityLocation: PropTypes.string,
  puppyName: PropTypes.string
};

TransportationModal.defaultProps = {
  breederFirstName: null,
  facilityLocation: null,
  puppyName: null,
  deliveryMethods: null
};
